import { useQuery } from "@tanstack/react-query";
import { Calendar, Users, Globe, Clock, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { buildApiUrl } from '@/lib/config';
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import { truncateText } from "@/lib/textUtils";
import { analyticsEvents, trackEvent } from "@/lib/ga4";

interface Package {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  capacity: string;
  type: string;
  image: string;
  imageUrl: string;
  tags: string[];
  featured: boolean;
}

export default function PackagesSection() {
  const { data: packages, isLoading } = useQuery<Package[]>({
    queryKey: ['/api/packages', 'featured'],
    queryFn: async () => {
      const response = await fetch(buildApiUrl('api/packages?featured=true'));
      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }
      return response.json();
    },
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const recordPackageClick = (pkg: Package) => {
    trackEvent({
      name: analyticsEvents.selectItem,
      params: {
        content_type: "package",
        package_id: pkg.id,
        package_name: pkg.title,
        price_inr: pkg.price,
        duration: pkg.duration,
        page_path: "/",
        page_type: "home",
        item_id: pkg.id,
        item_name: pkg.title,
      },
    });
  };

  const recordCta = (label: string) => {
    trackEvent({
      name: analyticsEvents.ctaClick,
      params: {
        content_type: "cta",
        item_id: label,
        item_name: label,
        cta_label: label,
        page_path: "/",
        destination_url: "/packages",
        page_type: "home",
      },
    });
  };

  if (isLoading) {
    return (
      <section id="packages" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Holiday Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Loading amazing packages...</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
                <div className="w-full h-64 bg-gray-300"></div>
                <div className="p-8">
                  <div className="h-6 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-6"></div>
                  <div className="h-12 bg-gray-300 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="packages" className="py-20 bg-gradient-to-b from-white to-tw-orange-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium mb-4">
            <Globe className="w-4 h-4 mr-2" />
            Travel Packages
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Discover Your Next <span className="text-orange-600">Adventure</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore amazing destinations with our carefully crafted travel packages designed for unforgettable experiences
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages && packages.length > 0 ? packages.map((pkg) => (
            <Link
              key={pkg.id}
              href={`/packages/${pkg.id}`}
              aria-label={`${pkg.title}, view package details`}
              onClick={() => recordPackageClick(pkg)}
            >
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 group border border-gray-100 cursor-pointer h-full flex flex-col">
              <div className="relative overflow-hidden">
                <img
                  src={pkg.imageUrl || pkg.image}
                  alt={pkg.title}
                  width={960}
                  height={256}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-2xl font-bold text-orange-600">{formatPrice(pkg.price)}</span>
                </div>
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{pkg.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed flex-grow">{truncateText(pkg.description, 18)}</p>
                <div className="flex items-center text-gray-500 mb-6 space-x-6">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                    <span className="font-medium">{pkg.duration}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 mr-2 text-green-500" />
                    <span className="font-medium">{pkg.capacity}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {pkg.tags && Array.isArray(pkg.tags) && pkg.tags.slice(0, 3).map((tag: string, index: number) => (
                    <Badge key={index} className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 text-lg font-semibold rounded-xl mt-auto">
                  View Details
                </Button>
              </div>
              </div>
            </Link>
          )) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 text-lg">No packages available at the moment.</p>
            </div>
          )}
        </div>

        <div className="text-center mt-16">
          <Link to="/packages">
            <Button
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-12 py-4 text-lg font-semibold rounded-xl"
              onClick={() => recordCta("packages_view_all")}
            >
              <Globe className="w-6 h-6 mr-3" />
              Explore All Destinations
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}